/**
 * Pure builder-form logic — the JSX-free half so persona/appearance validation
 * and the describe/upload submit shape are unit-tested without a DOM. The builder
 * modal collects the SAME `Persona` + `Appearance` the wizard does and validates
 * against the one `@4f/shared` source of truth (adult-only `AgeRange` is
 * structural, so a minor persona is unrepresentable — compliance red line 1). No
 * consistency logic lives here: the pinned seed + reference are the server's job.
 */
import {
  AgeRange,
  Appearance,
  AppearanceStyle,
  type BuildSource,
  NsfwLevel,
  Persona,
} from '@4f/shared';

/** The two builder paths — generate her look from text, or ingest your own concept image. */
export type BuilderMode = BuildSource;

/** Flat, controlled form state (all strings — controls are text/select). */
export interface BuilderForm {
  // Persona
  name: string;
  nsfwLevel: string;
  traits: string;
  backstory: string;
  relationship: string;
  speakingStyle: string;
  // Appearance
  style: string;
  body: string;
  hair: string;
  eyes: string;
  ethnicity: string;
  ageRange: string;
  outfit: string;
}

/** A blank form seeded with the first valid option for each enum control. */
export function emptyBuilderForm(): BuilderForm {
  return {
    name: '',
    nsfwLevel: NsfwLevel.options[0],
    traits: '',
    backstory: '',
    relationship: '',
    speakingStyle: '',
    style: AppearanceStyle.options[0],
    body: '',
    hair: '',
    eyes: '',
    ethnicity: '',
    ageRange: AgeRange.options[0],
    outfit: '',
  };
}

/** Split a comma-separated free-text field into trimmed, non-empty tokens. */
export function splitTags(raw: string): string[] {
  return raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

/** Compile the persona fields into a validated {@link Persona}, or `null` when invalid. */
export function buildPersona(form: BuilderForm): Persona | null {
  const parsed = Persona.safeParse({
    name: form.name.trim(),
    traits: splitTags(form.traits),
    backstory: form.backstory.trim(),
    relationship: form.relationship.trim(),
    speakingStyle: form.speakingStyle.trim(),
    nsfwLevel: form.nsfwLevel,
  });
  return parsed.success ? parsed.data : null;
}

/** Compile the appearance fields into a validated {@link Appearance}, or `null` when invalid. */
export function buildAppearance(form: BuilderForm): Appearance | null {
  const outfit = form.outfit.trim();
  const parsed = Appearance.safeParse({
    style: form.style,
    body: form.body.trim(),
    hair: form.hair.trim(),
    eyes: form.eyes.trim(),
    ethnicity: form.ethnicity.trim(),
    ageRange: form.ageRange,
    ...(outfit ? { outfit } : {}),
    tags: [],
  });
  return parsed.success ? parsed.data : null;
}

/**
 * The names of the fields that fail validation for a given mode — empty ⇒ the
 * form is submittable. Appearance is required for BOTH paths (the create body
 * always carries one); the upload path additionally requires a concept image,
 * checked by the caller (it owns the `File`).
 */
export function invalidBuilderFields(form: BuilderForm): string[] {
  const invalid: string[] = [];
  if (!buildPersona(form)) {
    // The only hard-required persona field is the name (rest default).
    if (form.name.trim().length === 0) {
      invalid.push('name');
    } else {
      invalid.push('persona');
    }
  }
  if (!buildAppearance(form)) {
    for (const field of ['body', 'hair', 'eyes', 'ethnicity'] as const) {
      if (form[field].trim().length === 0) {
        invalid.push(field);
      }
    }
    if (invalid.length === 0) {
      invalid.push('appearance');
    }
  }
  return invalid;
}
